<template>
  <div class="app-main-content provision-rule-tab">
    <div class="grid-view">
      <div class="main-content">
        <search-item
          ref="searchItem"
          config-module="25002004"
          :show-simple="false"
          :show-adv="true"
          :expand-adv="true"
          :auto-init="false"
          :custom-search-items="customSearchItems"
          @advSearchItemCallBack="onAdvancedQuery"
          @resetFields="onResetFields"
        >
          <div slot="operations">
            <el-button type="primary" @click="addHandler">新增</el-button>
            <el-button type="primary" @click="batchAddHandler">批量新增</el-button>
            <el-button type="primary" @click="editHandler">编辑</el-button>
          </div>
        </search-item>
        <div class="section grid-wrapper">
          <cargo-ag-grid
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="pageRowData"
            style="width: 100%; height: 100%; min-height: 360px;"
            @gridReady="params => { gridApi = params.api }"
          />
        </div>
        <div class="pagination-wrapper">
          <cargo-pagination
            :total="filter.total"
            :page-count="pageCount"
            :page.sync="filter.pageNum"
            :limit.sync="filter.pageSize"
            @pagination="loadData"
          />
        </div>
      </div>
    </div>

    <person-cost-edit-dialog
      v-if="editDialogVisible"
      :visible.sync="editDialogVisible"
      :header-row="editingHeader"
      :item-row="editingItem"
      @success="loadData"
    />
    <person-cost-batch-dialog
      v-if="batchDialogVisible"
      :visible.sync="batchDialogVisible"
      @success="loadData"
    />
    <person-cost-detail-dialog
      :visible.sync="detailDialogVisible"
      :header-uuid="detailHeaderUuid"
      @edit-item="onEditItemFromDetail"
    />
  </div>
</template>

<script>
import searchItem from '@/components/Cargo/Common/cargoCombinationSearch'
import PersonCostEditDialog from '../components/PersonCostEditDialog.vue'
import PersonCostBatchDialog from '../components/PersonCostBatchDialog.vue'
import PersonCostDetailDialog from '../components/PersonCostDetailDialog.vue'
import { queryPersonCosts } from '@/api/provision/provisionRule'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'
import {
  applySearchConditions,
  createSelectSearchField,
  createUserSearchField,
  initLocalSearchItem
} from '../utils/provisionRuleSearch'

export default {
  name: 'PersonCostTab',
  components: { searchItem, PersonCostEditDialog, PersonCostBatchDialog, PersonCostDetailDialog },
  data() {
    return {
      customSearchItems: [
        createSelectSearchField('岗位', 'stationName', ProvisionConstants.CONFIGURABLE_STATIONS),
        createUserSearchField('人员', 'userId'),
        createSelectSearchField('总成本是否为0', 'zeroCostFlag', ProvisionConstants.ZERO_COST_FLAG_OPTIONS)
      ],
      filter: {
        stationName: '',
        userId: '',
        zeroCostFlag: '',
        pageNum: 1,
        pageSize: 50,
        total: 0
      },
      allRowData: [],
      gridApi: null,
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        onRowDoubleClicked: (event) => this.openDetail(event.data)
      },
      columnDefs: [
        { headerName: '岗位', field: 'stationName', width: 120 },
        { headerName: '人员', field: 'userName', width: 120 },
        {
          headerName: '创建日期',
          field: 'dateCreated',
          width: 160,
          valueFormatter: p => this.formatDate(p.value)
        },
        {
          headerName: '更新日期',
          field: 'dateModified',
          width: 160,
          valueFormatter: p => this.formatDate(p.value)
        },
        {
          headerName: '总成本',
          field: 'totalCost',
          width: 120,
          valueFormatter: p => (p.value == null ? '0' : Number(p.value).toFixed(2))
        },
        {
          headerName: '操作',
          width: 100,
          cellRenderer: params => {
            const data = params.data || {}
            const detailBtn = document.createElement('a')
            detailBtn.innerText = '查看详情'
            detailBtn.onclick = (e) => {
              e.stopPropagation()
              this.openDetail(data)
            }
            return detailBtn
          }
        }
      ],
      editDialogVisible: false,
      batchDialogVisible: false,
      detailDialogVisible: false,
      editingHeader: null,
      editingItem: null,
      detailHeaderUuid: ''
    }
  },
  computed: {
    pageRowData() {
      const start = (this.filter.pageNum - 1) * this.filter.pageSize
      return this.allRowData.slice(start, start + this.filter.pageSize)
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filter.total / this.filter.pageSize) || 1)
    }
  },
  methods: {
    init() {
      this.$nextTick(() => initLocalSearchItem(this.$refs.searchItem))
      this.loadData()
    },
    formatDate(val) {
      if (!val) return ''
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return val
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    onAdvancedQuery(conditions) {
      applySearchConditions(this.filter, conditions,
        ['stationName', 'userId', 'zeroCostFlag'])
      this.filter.pageNum = 1
      this.loadData()
    },
    onResetFields() {
      applySearchConditions(this.filter, [],
        ['stationName', 'userId', 'zeroCostFlag'])
      this.filter.pageNum = 1
      this.loadData()
    },
    async loadData() {
      try {
        const payload = {
          stationName: this.filter.stationName || undefined,
          userId: this.filter.userId || undefined,
          zeroCostFlag: this.filter.zeroCostFlag || undefined
        }
        const res = await queryPersonCosts(payload)
        if (!res.ok) {
          this.$message.error('加载人员成本失败')
          return
        }
        this.allRowData = res.data || []
        this.filter.total = this.allRowData.length
        if (this.filter.pageNum > this.pageCount) {
          this.filter.pageNum = 1
        }
      } catch (e) {
        console.error(e)
        this.$message.error('加载人员成本失败')
      }
    },
    addHandler() {
      this.editingHeader = null
      this.editingItem = null
      this.editDialogVisible = true
    },
    batchAddHandler() {
      this.batchDialogVisible = true
    },
    editHandler() {
      const row = this.getSelectedRow()
      if (!row) return
      this.openEditHeader(row)
    },
    getSelectedRow() {
      const rows = this.gridApi ? this.gridApi.getSelectedRows() : []
      if (!rows.length) {
        this.$message.warning('请先选择一条记录')
        return null
      }
      return rows[0]
    },
    openDetail(row) {
      if (!row || !row.uuid) return
      this.detailHeaderUuid = row.uuid
      this.detailDialogVisible = true
    },
    openEditHeader(row) {
      this.editingHeader = row
      this.editingItem = null
      this.editDialogVisible = true
    },
    onEditItemFromDetail(item, header) {
      this.detailDialogVisible = false
      this.editingHeader = header
      this.editingItem = item
      this.editDialogVisible = true
    }
  }
}
</script>

<style scoped>
.grid-wrapper {
  flex-grow: 1;
  min-height: 360px;
}
.pagination-wrapper {
  margin-top: 8px;
  text-align: right;
}
</style>
